import HyperDX from '@hyperdx/browser';

// Custom logging functions for HyperDX integration - Optimized for performance
export const hyperDXLogger = {
	// Log user actions - throttled for performance
	logUserAction: throttle((action: string, data?: Record<string, any>) => {
		if (isHyperDXInitialized()) {
			// Use HyperDX.addAction instead of console.log for better performance
			HyperDX.addAction(action, data);
		}
	}, 1000), // Throttle to max 1 action per second

	// Log API calls - only for errors and important requests
	logApiCall: (method: string, url: string, status?: number, data?: Record<string, any>) => {
		if (isHyperDXInitialized() && ((status !== undefined && status >= 500) || url.includes('error'))) {
			// Only log failed API calls to reduce noise
			HyperDX.addAction('api_error', { method, url, status, ...data });
		}
	},

	// Log custom events - debounced for performance
	logEvent: debounce((event: string, data?: Record<string, any>) => {
		if (isHyperDXInitialized()) {
			HyperDX.addAction(event, data);
		}
	}, 500), // Debounce to prevent spam

	// Log errors manually - only console.error for HyperDX to capture
	logError: (error: Error, context?: Record<string, any>) => {
		if (isHyperDXInitialized()) {
			console.error('[ERROR]', error.message, { 
				stack: error.stack, 
				...context 
			});
		}
	},

	// Log warnings - reduced frequency
	logWarning: throttle((message: string, data?: Record<string, any>) => {
		if (isHyperDXInitialized()) {
			console.warn(`[WARNING] ${message}`, data);
		}
	}, 2000), // Max 1 warning per 2 seconds

	// Log performance metrics - only significant metrics
	logPerformance: (metric: string, value: number, unit: string = 'ms') => {
		if (isHyperDXInitialized() && value > 1000) { // Only log slow operations (>1s)
			HyperDX.addAction('performance_metric', { metric, value, unit });
		}
	},

	// Log user authentication events - important events only
	logAuth: (event: 'login' | 'logout' | 'register' | 'failed_login', userId?: string) => {
		if (isHyperDXInitialized()) {
			HyperDX.addAction(`auth_${event}`, { userId });
		}
	},

	// Log page views - throttled
	logPageView: throttle((path: string, title?: string) => {
		if (isHyperDXInitialized()) {
			HyperDX.addAction('page_view', { path, title });
		}
	}, 1000), // Max 1 page view per second

	// Log feature usage - debounced
	logFeatureUsage: debounce((feature: string, data?: Record<string, any>) => {
		if (isHyperDXInitialized()) {
			HyperDX.addAction(`feature_${feature}`, data);
		}
	}, 1000), // Debounce feature usage logs
};

// Utility functions for throttling and debouncing
function throttle<T extends (...args: any[]) => any>(func: T, limit: number): T {
	let inThrottle: boolean;
	return ((...args: Parameters<T>) => {
		if (!inThrottle) {
			func(...args);
			inThrottle = true;
			setTimeout(() => inThrottle = false, limit);
		}
	}) as T;
}

function debounce<T extends (...args: any[]) => any>(func: T, delay: number): T {
	let timeoutId: NodeJS.Timeout;
	return ((...args: Parameters<T>) => {
		clearTimeout(timeoutId);
		timeoutId = setTimeout(() => func(...args), delay);
	}) as T;
}

// Performance monitoring helpers
export const performanceMonitor = {
	// Measure function execution time
	measureAsync: async <T>(
		name: string,
		fn: () => Promise<T>
	): Promise<T> => {
		const start = performance.now();
		try {
			const result = await fn();
			const duration = performance.now() - start;
			hyperDXLogger.logPerformance(name, duration);
			return result;
		} catch (error) {
			const duration = performance.now() - start;
			hyperDXLogger.logPerformance(`${name}_error`, duration);
			throw error;
		}
	},

	// Measure synchronous function execution time
	measure: <T>(name: string, fn: () => T): T => {
		const start = performance.now();
		try {
			const result = fn();
			const duration = performance.now() - start;
			hyperDXLogger.logPerformance(name, duration);
			return result;
		} catch (error) {
			const duration = performance.now() - start;
			hyperDXLogger.logPerformance(`${name}_error`, duration);
			throw error;
		}
	},

	// Mark navigation timing
	markNavigation: (name: string) => {
		if (performance.mark) {
			performance.mark(`navigation_${name}`);
			hyperDXLogger.logEvent('navigation_mark', { name });
		}
	},
};

// User context helpers
export const userContext = {
	// Set user information with HyperDX identification format: '<firstname lastname>-<user-id>'
	setUser: (userId: string, email?: string, firstName?: string, lastName?: string, additionalProperties?: Record<string, any>) => {
		if (isHyperDXInitialized()) {
			// Create user display name for identification purposes
			const userDisplayName = firstName && lastName ? `${firstName} ${lastName}` : '';
			const userIdentification = userDisplayName ? `${userDisplayName}-${userId}` : userId;
			
			// Set HyperDX global attributes for user identification
			const attributes: Record<string, any> = {
				userId: userId, // Keep userId as just the raw user ID
				userIdentification: userIdentification, // Add separate field for formatted identification
				...additionalProperties,
			};
			
			// Only add optional properties if they exist
			if (email) attributes.userEmail = email;
			if (userDisplayName) attributes.userName = userDisplayName;
			if (firstName) attributes.userFirstName = firstName;
			if (lastName) attributes.userLastName = lastName;
			
			HyperDX.setGlobalAttributes(attributes);
			
			hyperDXLogger.logEvent('user_context_set', { 
				userId: userId, // Log just the raw user ID
				userIdentification: userIdentification, // Log the formatted identification separately
				email, 
				firstName, 
				lastName,
				...additionalProperties 
			});
		}
	},

	// Clear user information
	clearUser: () => {
		if (isHyperDXInitialized()) {
			// Clear HyperDX global attributes by setting them to empty strings
			HyperDX.setGlobalAttributes({
				userId: '',
				userIdentification: '',
				userEmail: '',
				userName: '',
				userFirstName: '',
				userLastName: '',
			});
			
			hyperDXLogger.logEvent('user_context_cleared');
		}
	},

	// Set user properties
	setUserProperties: (properties: Record<string, any>) => {
		if (isHyperDXInitialized()) {
			HyperDX.setGlobalAttributes(properties);
			hyperDXLogger.logEvent('user_properties_set', properties);
		}
	},
};

// Error handling helpers
export const errorHandler = {
	// Handle and log async errors
	handleAsyncError: (error: Error, context?: Record<string, any>) => {
		hyperDXLogger.logError(error, context);
		// You can also send to other error tracking services here
	},

	// Wrap async functions with error handling
	wrapAsync: <T extends any[], R>(
		fn: (...args: T) => Promise<R>,
		context?: Record<string, any>
	) => {
		return async (...args: T): Promise<R> => {
			try {
				return await fn(...args);
			} catch (error) {
				errorHandler.handleAsyncError(error as Error, context);
				throw error;
			}
		};
	},
};

// Check if HyperDX is initialized
export const isHyperDXInitialized = (): boolean => {
	const isProduction = process.env.REACT_APP_ENV === 'prod' || process.env.NODE_ENV === 'production';
	const isHyperDXExplicitlyEnabled = process.env.REACT_APP_HYPERDX_ENABLED === 'true';
	const isHyperDXExplicitlyDisabled = process.env.REACT_APP_HYPERDX_ENABLED === 'false';
	
	// If explicitly disabled, return false regardless of other conditions
	if (isHyperDXExplicitlyDisabled) {
		return false;
	}
	
	return (isProduction || isHyperDXExplicitlyEnabled) && Boolean(process.env.REACT_APP_HYPERDX_API_KEY);
};

// Export HyperDX instance for direct access if needed
export { HyperDX };

// Export default logger instance
export default hyperDXLogger; 